import { useNavigate } from 'react-router-dom'
import {
  faCircleQuarterStroke,
  faRedo,
} from '@fortawesome/pro-regular-svg-icons'
import {
  QuarterlyTaxEstimateDetail,
  QuarterlyTaxEstimateDetailStatuses,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { UserTaxCalculation } from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'

import { useReselector } from '@/utils/sharedHooks'
import { selectFederalEstimateByTaxQuarter } from '@/features/taxes/quarterly/userTaxEstimates.selector'

import { ZeroEstimateTooltip } from '../EstimateBreakdownV3Tooltips'
import { PaymentCard } from '../PaymentCard'
import { centsToDollarsOrDashes } from '../utils'
import {
  ROUND_UP_INCREMENT_LABEL,
  reconcileEstimateDisplay,
} from '../estimateReconciliation'
import { useBreakdownTransparency } from '../useBreakdownTransparency'
import { PlainSummaryCard } from '../PlainSummaryCard'
import { federalSummarySentences } from '../plainSummary'

// Extracted components
import { IncomeSection } from './income-section'
import { TaxesSection } from './taxes-section'
import { TaxesPaidToDateSection } from './taxes-paid-section'
import { BreakdownTooltip } from './shared/breakdown-tooltip'
import { CalculationRow } from './shared/calculation-row'
import { QTEFAQs } from './shared/qte-faqs'

import { TextColors, TypographyStyles } from '@/styles'
import {
  Button,
  Card,
  Icon,
  Text,
  Link,
  Alert,
  CardColors,
  Separator,
} from '@/ui'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'

const EstimateBreakdownV3 = ({
  taxCalc,
  estimateDetails,
  isSample,
}: {
  taxCalc: UserTaxCalculation | undefined
  estimateDetails: QuarterlyTaxEstimateDetail | undefined
  isSample?: boolean
}) => {
  const quarter = estimateDetails?.taxQuarter

  const isActiveEstimate =
    estimateDetails?.status === QuarterlyTaxEstimateDetailStatuses.active
  const navigate = useNavigate()
  const transparencyEnabled = useBreakdownTransparency()

  const federalTaxEstimate = useReselector(
    selectFederalEstimateByTaxQuarter,
    `${estimateDetails?.taxYear}-${estimateDetails?.taxQuarter}`
  )

  const minimumPaymentInCents = federalTaxEstimate?.minimumPaymentInCents

  const reconciliation = reconcileEstimateDisplay({
    calcAmountInCents: taxCalc?.federalQuarterlyEstimateInCents,
    releasedAmountInCents:
      transparencyEnabled && !isSample
        ? federalTaxEstimate?.estimateInCents
        : undefined,
  })

  const estimateText = reconciliation.displayInCents ? (
    <Text variant={TypographyStyles.H3}>
      {centsToDollarsOrDashes(reconciliation.displayInCents)}
    </Text>
  ) : reconciliation.displayInCents === 0 ? (
    <BreakdownTooltip
      trigger={centsToDollarsOrDashes(reconciliation.displayInCents)}
      content={<ZeroEstimateTooltip />}
    />
  ) : (
    <Text variant={TypographyStyles.H3}>--</Text>
  )

  return (
    <>
      {/* Eyebrow label */}
      <Text variant={TypographyStyles.EYEBROW} className="text-muted mb-2">
        federal
      </Text>

      {/* Page title */}
      <Text variant={TypographyStyles.H1} className="mb-6">
        Q{quarter} Federal Tax Estimate Breakdown
      </Text>

      {/* Two-column responsive layout */}
      <div className="flex flex-col lg:flex-row lg:space-x-8">
        {/* Main content column (spans 2/3 on lg) */}
        <div className="w-full lg:w-2/3 flex flex-col space-y-6">
          <PlainSummaryCard
            sentences={federalSummarySentences({
              taxCalc,
              estimateDetails,
              displayInCents: reconciliation.displayInCents,
            })}
          />

          <IncomeSection
            taxCalc={taxCalc}
            estimateDetails={estimateDetails}
            isSample={isSample}
          />

          <TaxesSection
            taxCalc={taxCalc}
            estimateDetails={estimateDetails}
            isSample={isSample}
          />

          <TaxesPaidToDateSection
            taxCalc={taxCalc}
            estimateDetails={estimateDetails}
            isSample={isSample}
          />

          <Separator />

          <Card color={CardColors.LEVEL_1}>
            {reconciliation.roundingDeltaInCents !== null && (
              <CalculationRow
                left={
                  <Text color={TextColors.MUTED}>
                    Rounded up to the nearest {ROUND_UP_INCREMENT_LABEL}
                    {reconciliation.preRoundingInCents !== null &&
                      ` from ${formatCurrencyFromCents(
                        reconciliation.preRoundingInCents
                      )}`}
                  </Text>
                }
                right={
                  <Text color={TextColors.MUTED}>
                    {centsToDollarsOrDashes(
                      reconciliation.roundingDeltaInCents
                    )}
                  </Text>
                }
                inCard
              />
            )}
            <CalculationRow
              left={
                <Text variant={TypographyStyles.H2}>
                  <span className="inline-flex items-center">
                    <Icon
                      icon={faCircleQuarterStroke}
                      className="mr-2 transform rotate-[90deg]"
                    />
                    Q{quarter} Quarterly Tax Estimate
                  </span>
                </Text>
              }
              right={estimateText}
              inCard
            />
            {reconciliation.releasedAmountDiverges && (
              <Text
                variant={TypographyStyles.BODY_SM}
                color={TextColors.MUTED}
                className="mt-2"
              >
                This is the amount released for this quarter. It differs from
                the calculation above, which may have been updated since.
              </Text>
            )}
            {transparencyEnabled && minimumPaymentInCents && (
              <Text
                variant={TypographyStyles.BODY_SM}
                color={TextColors.MUTED}
                className="mt-2"
              >
                The smallest payment that avoids an IRS penalty, based on what
                you owed last year:{' '}
                {centsToDollarsOrDashes(minimumPaymentInCents)}. The amount
                above is based on what you&apos;re earning this year instead,
                which is why the two differ.{' '}
                <Link href="/taxes/quarterly/calculate-safe-harbor">
                  Review your safe harbor
                </Link>
              </Text>
            )}
            {transparencyEnabled &&
              reconciliation.displayInCents === 0 &&
              quarter && (
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={TextColors.MUTED}
                  className="mt-2"
                >
                  Your tax withholdings and quarterly payments already cover the{' '}
                  {Number(quarter) * 25}% of your projected annual tax due by Q
                  {quarter}, so there&apos;s nothing to pay this quarter.
                </Text>
              )}
          </Card>

          {isActiveEstimate && (
            <Alert className="mt-4">
              <div>
                Any changes made to your Profit & Loss statement, transactions
                or categorizations since this estimate was released won’t be
                applied until you select{' '}
                <Link disabled={isSample} href="/taxes/quarterly/recalculate">
                  Make Adjustments
                </Link>{' '}
                again.
              </div>
            </Alert>
          )}
        </div>

        {/* Sidebar column (spans 1/3 on lg) */}
        <div className="w-full lg:w-1/3 flex flex-col space-y-6">
          <Text variant={TypographyStyles.H2}>
            Q{quarter} Federal Tax Estimate
          </Text>

          <PaymentCard
            taxCalc={taxCalc}
            estimateDetails={estimateDetails}
            isSample={isSample}
          />

          {isActiveEstimate && (
            <Card color={CardColors.LEVEL_1}>
              <div className="flex flex-col space-y-4 p-4">
                <Text variant={TypographyStyles.H3}>
                  Need to make an adjustment to your tax information?
                </Text>
                <Text>
                  If you need to change your income or withholdings, you can do
                  so and receive a new estimate.
                </Text>
                <Button
                  variant="secondary"
                  onClick={() => navigate('/taxes/quarterly/recalculate')}
                  disabled={isSample}
                >
                  <Icon icon={faRedo} className="mr-2" />
                  Make Adjustments
                </Button>
              </div>
            </Card>
          )}

          <QTEFAQs />
        </div>
      </div>
    </>
  )
}

export default EstimateBreakdownV3
